import { Box, Flex, Link, Table, Tbody, Text, Tr, Th } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import throttle from 'lodash/throttle';
import React from 'react';

import type { Transaction, TransactionsSortingField, TransactionsSortingValue } from 'types/api/transaction';

import config from 'configs/app';
import { AddressHighlightProvider } from 'lib/contexts/addressHighlight';
import useLazyRenderedList from 'lib/hooks/useLazyRenderedList';
import { useI18n } from 'lib/i18n/context';
import { getSocketNoticeKey } from 'lib/i18n/socketNotice';
import { currencyUnits } from 'lib/units';
import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';
import * as SocketNewItemsNotice from 'ui/shared/SocketNewItemsNotice';
import TheadSticky from 'ui/shared/TheadSticky';

import { TXS_HEADER_KEYS } from './i18n';
import TxsTableItem from './TxsTableItem';

const EXPLORER_HEADER_BG = '#111111';
const EXPLORER_BORDER_COLOR = '#222222';
const EXPLORER_TITLE_COLOR = '#FFFFFF';
const EXPLORER_NOTICE_GRADIENT = 'linear-gradient(90deg, rgba(20, 220, 172, 1) 0%, rgba(20, 20, 20, 1) 100%)';

const getExplorerColumns = (showBlockInfo: boolean) => [
  '180px',
  '132px',
  '132px',
  ...(showBlockInfo ? [ '96px' ] : []),
  '96px',
  '1fr',
  ...(!config.UI.views.tx.hiddenFields?.value ? [ '120px' ] : []),
  ...(!config.UI.views.tx.hiddenFields?.tx_fee ? [ '120px' ] : []),
];

type Props = {
  txs: Array<Transaction>;
  sort: (field: TransactionsSortingField) => () => void;
  sorting?: TransactionsSortingValue;
  top: number;
  stickySurface?: boolean;
  showBlockInfo: boolean;
  showSocketInfo: boolean;
  socketInfoAlert?: string;
  socketInfoNum?: number;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  variant?: 'default' | 'explorer';
  desktopTopSlot?: React.ReactNode;
};

const TxsTable = ({
  txs,
  // sort,
  // sorting,
  top,
  stickySurface,
  showBlockInfo,
  showSocketInfo,
  socketInfoAlert,
  socketInfoNum,
  currentAddress,
  enableTimeIncrement,
  isLoading,
  variant = 'default',
  desktopTopSlot,
}: Props) => {
  const { t } = useI18n();
  const { cutRef, renderedItemsNum } = useLazyRenderedList(txs, !isLoading);
  const isExplorer = variant === 'explorer';
  const explorerColumns = React.useMemo(() => getExplorerColumns(showBlockInfo).join(' '), [ showBlockInfo ]);
  const explorerHeaderRef = React.useRef<HTMLDivElement>(null);
  const [ isExplorerHeaderSticky, setIsExplorerHeaderSticky ] = React.useState(false);

  const feeCurrency = config.UI.views.tx.hiddenFields?.fee_currency || config.chain.hasMultipleGasCurrencies ?
    '' :
    ' ' + currencyUnits.ether;

  React.useEffect(() => {
    if (!isExplorer) {
      return;
    }

    const handleScroll = () => {
      setIsExplorerHeaderSticky(Number(explorerHeaderRef.current?.getBoundingClientRect().top) <= top);
    };

    const throttledHandleScroll = throttle(handleScroll, 100);
    handleScroll();
    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [ isExplorer, top ]);

  const explorerHeader = (
    <Box
      ref={ explorerHeaderRef }
      position="sticky"
      top={ top ? `${ top }px` : 0 }
      zIndex={ 2 }
      bg={ EXPLORER_HEADER_BG }
      borderBottom="1px solid"
      borderColor={ EXPLORER_BORDER_COLOR }
      boxShadow={ isExplorerHeaderSticky ? '0 10px 28px rgba(0, 0, 0, 0.4)' : 'none' }
      transition="box-shadow 0.2s ease"
    >
      { desktopTopSlot ? (
        <Box px="16px" py="12px">
          { desktopTopSlot }
        </Box>
      ) : null }
      { showSocketInfo && (isLoading || socketInfoAlert || socketInfoNum) ? (
        <Box px="16px" pb="8px">
          { isLoading ? (
            <Skeleton h="22px" borderRadius="2px"/>
          ) : (
            <Flex
              h="22px"
              px="16px"
              alignItems="center"
              bg={ EXPLORER_NOTICE_GRADIENT }
              borderRadius="2px"
              color={ EXPLORER_TITLE_COLOR }
              fontSize="14px"
              lineHeight="20px"
              fontWeight={ 400 }
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            >
              { (() => {
                if (socketInfoAlert) {
                  return socketInfoAlert;
                }

                if (socketInfoNum) {
                  return (
                    <>
                      <Link href={ window.location.href } color="#14DCAC" _hover={{ color: '#14DCAC', textDecoration: 'none' }}>
                        { t(getSocketNoticeKey('transaction', socketInfoNum), { count: socketInfoNum.toLocaleString() }) }
                      </Link>
                      <Text as="span" color={ EXPLORER_TITLE_COLOR }> { t('common.socketNotice.haveComeIn') }</Text>
                    </>
                  );
                }

                return t('common.socketNotice.scanningTransactions');
              })() }
            </Flex>
          ) }
        </Box>
      ) : null }
      <Box
        display="grid"
        gridTemplateColumns={ explorerColumns }
        columnGap="16px"
        alignItems="center"
        px="16px"
        py="14px"
      >
        <Flex alignItems="center" gap="10px">
          <IconSvg name="info" boxSize="14px" color="#999999"/>
          <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t(TXS_HEADER_KEYS.hash) }</Text>
        </Flex>
        <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t('common.type') }</Text>
        <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t(TXS_HEADER_KEYS.method) }</Text>
        { showBlockInfo ? (
          <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t('common.block') }</Text>
        ) : null }
        <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t('common.age') }</Text>
        <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 }>{ t(TXS_HEADER_KEYS.fromTo) }</Text>
        { !config.UI.views.tx.hiddenFields?.value ? (
          <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 } textAlign="right">
            { t('txs.valueCurrencyunitsether', { currencyUnitsEther: currencyUnits.ether }) }
          </Text>
        ) : null }
        { !config.UI.views.tx.hiddenFields?.tx_fee ? (
          <Text color={ EXPLORER_TITLE_COLOR } fontSize="14px" lineHeight="22px" fontWeight={ 700 } textAlign="right">
            { t('txs.feeValue', { value: feeCurrency.trim() || currencyUnits.ether }) }
          </Text>
        ) : null }
      </Box>
    </Box>
  );

  return (
    <AddressHighlightProvider>
      { isExplorer ? explorerHeader : null }
      <Box px={ isExplorer ? '16px' : 0 } pb={ isExplorer ? '8px' : 0 }>
        <Table
          minWidth={ isExplorer ? '1120px' : '1100px' }
          width="100%"
          sx={{
            tableLayout: isExplorer ? 'fixed' : 'auto',
            borderCollapse: 'separate',
            borderSpacing: isExplorer ? '0' : '0 8px',
          }}
        >
          { isExplorer ? (
            <Box as="colgroup">
              <Box as="col" width="180px"/>
              <Box as="col" width="132px"/>
              <Box as="col" width="132px"/>
              { showBlockInfo ? <Box as="col" width="96px"/> : null }
              <Box as="col" width="96px"/>
              <Box as="col"/>
              { !config.UI.views.tx.hiddenFields?.value ? <Box as="col" width="120px"/> : null }
              { !config.UI.views.tx.hiddenFields?.tx_fee ? <Box as="col" width="120px"/> : null }
            </Box>
          ) : null }
          { !isExplorer ? (
            <TheadSticky top={ top } stickySurface={ stickySurface }>
              <Tr>
                <Th width="136px">{ t(TXS_HEADER_KEYS.hash) }</Th>
                <Th width="96px">{ t(TXS_HEADER_KEYS.dateTime) }</Th>
                { showBlockInfo && <Th width="96px">{ t('common.block') }</Th> }
                <Th width="208px">{ t(TXS_HEADER_KEYS.fromTo) }</Th>
                <Th width="110px">{ t(TXS_HEADER_KEYS.amount) }</Th>
                <Th width="110px">{ t(TXS_HEADER_KEYS.method) }</Th>
                <Th width="110px" isNumeric>{ t(TXS_HEADER_KEYS.valueUsd) }</Th>
                <Th width="110px" isNumeric pr={ 5 }>{ t('txs.txnFeeSymbol', { symbol: config.chain.currency.symbol }) }</Th>
              </Tr>
            </TheadSticky>
          ) : null }
          <Tbody>
            { !isExplorer && showSocketInfo ? (
              <SocketNewItemsNotice.Desktop
                url={ window.location.href }
                alert={ socketInfoAlert }
                num={ socketInfoNum }
                isLoading={ isLoading }
              />
            ) : null }
            <AnimatePresence initial={ false }>
              { txs.slice(0, renderedItemsNum).map((item, index) => (
                <TxsTableItem
                  key={ item.hash + (isLoading ? index : '') }
                  tx={ item }
                  showBlockInfo={ showBlockInfo }
                  currentAddress={ currentAddress }
                  enableTimeIncrement={ enableTimeIncrement }
                  isLoading={ isLoading }
                  variant={ variant }
                />
              )) }
            </AnimatePresence>
          </Tbody>
        </Table>
      </Box>
      <div ref={ cutRef }/>
    </AddressHighlightProvider>
  );
};

export default React.memo(TxsTable);
